import { useEffect, useState } from 'react';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useTenantStore } from '../stores/tenant.store';
import { useAuth } from '../contexts/auth.context';

/**
 * The caller's `Person` id in the active tenant — NOT their Auth uid.
 *
 * The two are different by construction: a person document is created first and
 * the Auth user is linked to it afterwards (`people/{personId}.authUid`). Every
 * academic record that points at a human — `Section.facultyId` among them —
 * stores the PERSON id, so comparing one against a uid silently matches
 * nothing.
 *
 * Resolution order, mirroring what the backend does:
 *   1. `membership.personId`, already loaded in the tenant store.
 *   2. `people where authUid == uid`, because that field is optional on the
 *      membership document and older ones do not carry it.
 *
 * Returns `null` while resolving AND when the person cannot be found. Callers
 * must treat `null` as "unknown", never as "nobody": filtering a list down to a
 * null id would hide everything.
 */
export function useCurrentPersonId(): string | null {
  const { user } = useAuth();
  const { currentTenantId, currentMembership } = useTenantStore();
  const fromMembership = (currentMembership as { personId?: string } | null)?.personId ?? null;
  const [resolved, setResolved] = useState<string | null>(null);

  useEffect(() => {
    if (fromMembership || !currentTenantId || !user?.uid) {
      setResolved(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, `tenants/${currentTenantId}/people`),
            where('authUid', '==', user.uid),
            limit(1),
          ),
        );
        if (!cancelled) setResolved(snap.empty ? null : (snap.docs[0]?.id ?? null));
      } catch {
        // Sin persona resoluble el llamador queda como "desconocido"; quien use
        // esto debe seguir funcionando sin filtrar.
        if (!cancelled) setResolved(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fromMembership, currentTenantId, user?.uid]);

  return fromMembership ?? resolved;
}
